import { Center, Drawer, Group, Loader, Text } from "@mantine/core"
import { useLocalStorage } from "@mantine/hooks"
import {
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
} from "react"
import { useTranslation } from "react-i18next"
import { useReminderPermission } from "./use-reminder-permission-for-reminder"
import { EditReminderDrawer } from "./-components/EditReminderDrawer"
import { ReminderViewDetailDrawer } from "./-components/ReminderViewDetailDrawer"
import { useGetAuthenticatedUser } from "@/routes/app/-components/use-authenticated-user"
import { ReminderDto } from "@/orval/gen"
import useGetReminderTitleComponent from "./use-get-reminder-title-component"

const MIN_DRAWER_WIDTH = 420
const DEFAULT_DRAWER_WIDTH = MIN_DRAWER_WIDTH
const MIN_PAGE_WIDTH = 360

export interface ReminderDetailDrawerProps {
  opened: boolean
  onClose: () => void
  reminder: ReminderDto
}

export function ReminderDetailDrawer({
  opened,
  onClose,
  reminder,
}: ReminderDetailDrawerProps) {
  const { permission, isLoading } = useReminderPermission(reminder.reminderId)
  const authenticatedUser = useGetAuthenticatedUser()
  const { t } = useTranslation("app_server")
  const [drawerWidth, setDrawerWidth] = useLocalStorage<number>({
    key: "reminder-detail-drawer-width",
    defaultValue: DEFAULT_DRAWER_WIDTH,
  })

  const TitleComponent = useGetReminderTitleComponent(
    reminder.objectTypeId,
    reminder.objectId,
    reminder.reminderId,
    { withLink: false }
  )
  const currentDrawerWidth = isLoading ? MIN_DRAWER_WIDTH : drawerWidth

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--reminder-drawer-width",
      `${currentDrawerWidth}px`
    )
  }, [currentDrawerWidth])

  const handleResizeStart = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      event.preventDefault()

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const nextWidth = window.innerWidth - moveEvent.clientX
        const maxWidth = Math.max(
          MIN_DRAWER_WIDTH,
          window.innerWidth - MIN_PAGE_WIDTH
        )

        setDrawerWidth(
          Math.max(MIN_DRAWER_WIDTH, Math.min(nextWidth, maxWidth))
        )
      }

      const handleMouseUp = () => {
        window.removeEventListener("mousemove", handleMouseMove)
        window.removeEventListener("mouseup", handleMouseUp)
        document.body.style.removeProperty("cursor")
        document.body.style.removeProperty("user-select")
      }

      document.body.style.cursor = "col-resize"
      document.body.style.userSelect = "none"
      window.addEventListener("mousemove", handleMouseMove)
      window.addEventListener("mouseup", handleMouseUp)
    },
    [setDrawerWidth]
  )

  const canEdit =
    permission === "Write" || reminder.userId == authenticatedUser.data?.userId

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={
        <Group gap={4} wrap="wrap">
          <Text c="inherit" fw={500} span>
            {t("ReminderTo")}:
          </Text>
          <Text c="inherit" span>
            <TitleComponent />
          </Text>
        </Group>
      }
      position="right"
      size={currentDrawerWidth}
      withOverlay={false}
      lockScroll={false}
      trapFocus={false}
      returnFocus={false}
      shadow="xl"
      styles={{
        content: {
          borderLeft: "1px solid var(--mantine-color-gray-6)",
          display: "flex",
          flexDirection: "column",
          overflowX: "visible",
          overflowY: "hidden",
        },
        body: {
          position: "relative",
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
        },
      }}
    >
      <div style={{ position: "relative", minHeight: "100%" }}>
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={t("ResizeReminderDrawer")}
          onMouseDown={handleResizeStart}
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: "calc(var(--mantine-spacing-md) * -1 - 2px)",
            width: "calc(var(--mantine-spacing-md) + 6px)",
            cursor: "col-resize",
            zIndex: 2,
            background: "transparent",
          }}
        />
        {canEdit ? (
          <EditReminderDrawer
            opened={opened}
            onClose={onClose}
            reminder={reminder}
          />
        ) : (
          <ReminderViewDetailDrawer
            opened={opened}
            onClose={onClose}
            reminder={reminder}
          />
        )}
      </div>
    </Drawer>
  )
}
